import { HeaderIcons } from '../../shared/icons';
import React from 'react';
import Header from '../../shared/Header';
import { DesktopLayout, StandingsSidebar, LiveAnswer } from '../../shared';
import { BeerTicker } from '../../shared/BeerTicker';
import { renderAvatar } from '../../../utils/avatarUtils';
import { useTranslation } from '../../../i18n';

interface DrinkerEntry {
  playerId: string;
  playerName: string;
  avatar?: string;
  vote: 'AGREE' | 'DISAGREE';
}

interface QuestionResult {
  questionId: string;
  optionA?: string;
  optionB?: string;
  optionACount?: number;
  optionBCount?: number;
  minorityChoice?: string | 'tie';
  debatePrompt?: string;
  drinkers?: DrinkerEntry[];
  leaderboard: Array<{
    playerId: string;
    playerName: string;
    score: number;
  }>;
}

interface WouldYouRatherResultScreenProps {
  questionResult: QuestionResult;
  roomState: {
    roomName: string;
    players: Array<{
      id: string;
      name: string;
      score: number;
      avatar?: string;
    }>;
  };
  liveAnswers: LiveAnswer[];
  onBackToGameSelection?: () => void;
  theme: {
    primary: string;
    primaryLight: string;
    primaryBorder: string;
    accent: string;
    accentLight: string;
    accentBorder: string;
  };
}

const THEME = {
  primary: '#EC4899',
  primaryLight: 'rgba(236, 72, 153, 0.15)',
  primaryBorder: 'rgba(236, 72, 153, 0.3)',
  accent: '#F97316',
  accentLight: 'rgba(249, 115, 22, 0.15)',
  accentBorder: 'rgba(249, 115, 22, 0.3)',
};

const fullscreenStyle: React.CSSProperties = {
  minHeight: '100vh',
  width: '100%',
  background: 'linear-gradient(135deg, #0a0a0f 0%, #1a0a2e 25%, #16213e 50%, #0f3460 75%, #1a0a2e 100%)',
  backgroundSize: '400% 400%',
  animation: 'gradientShift 15s ease infinite',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'hidden',
};

const contentStyle: React.CSSProperties = {
  flex: 1,
  padding: 'clamp(1rem, 3vw, 2.5rem) clamp(1rem, 3vw, 2rem)',
  paddingTop: 'clamp(90px, 10vw, 6rem)',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  zIndex: 2,
};

export const WouldYouRatherResultScreen: React.FC<WouldYouRatherResultScreenProps> = ({
  questionResult,
  roomState,
  liveAnswers,
  onBackToGameSelection,
  theme = THEME,
}) => {
  const { t } = useTranslation();
  const drinkers = questionResult.drinkers || [];
  const isTie = questionResult.minorityChoice === 'tie';
  const totalChoices = (questionResult.optionACount || 0) + (questionResult.optionBCount || 0);
  const optionA = questionResult.optionA || 'Option A';
  const optionB = questionResult.optionB || 'Option B';

  // Convert liveAnswers to shared format
  const sharedLiveAnswers: LiveAnswer[] = liveAnswers.map(la => ({
    playerId: la.playerId,
    playerName: la.playerName,
    avatar: la.avatar,
    timestamp: la.timestamp,
    answer: la.answer !== undefined ? String(la.answer) : undefined,
  }));

  return (
    <div style={fullscreenStyle}>
      <Header
        leftButton={onBackToGameSelection ? { icon: HeaderIcons.back, onClick: onBackToGameSelection, tooltip: t('gameplay.backToGameSelection') } : undefined}
        rightButton={{
          icon: HeaderIcons.refresh,
          onClick: () => window.location.reload(),
          tooltip: t('common.tooltips.refreshPage')
        }}
        roomCode={roomState.roomName}
      />
      <div style={contentStyle}>
        <DesktopLayout
          leftContent={
            <div style={{
              maxWidth: '1200px',
              margin: '0 auto',
              width: '100%',
            }}>
              {/* Choice Count Display */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: 'clamp(2rem, 5vw, 4rem)',
                marginBottom: '2rem',
                flexWrap: 'wrap',
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 'clamp(2.5rem, 7vw, 4rem)', fontWeight: 'bold', color: '#22C55E', lineHeight: 1 }}>
                    {questionResult.optionACount || 0}
                  </div>
                  <div style={{ fontSize: 'clamp(1.2rem, 3vw, 1.5rem)', color: '#22C55E', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                    A
                  </div>
                  <div style={{ fontSize: 'clamp(0.9rem, 2vw, 1rem)', opacity: 0.6, marginTop: '0.25rem', color: '#fff', maxWidth: '300px', lineHeight: '1.3' }}>
                    {optionA}
                  </div>
                  <div style={{ fontSize: 'clamp(0.9rem, 2vw, 1rem)', opacity: 0.6, marginTop: '0.25rem', color: '#fff' }}>
                    {totalChoices > 0 ? Math.round(((questionResult.optionACount || 0) / totalChoices) * 100) : 0}%
                  </div>
                </div>
                <div style={{ fontSize: 'clamp(2rem, 5vw, 3rem)', alignSelf: 'center', opacity: 0.3, color: '#fff' }}>VS</div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 'clamp(2.5rem, 7vw, 4rem)', fontWeight: 'bold', color: '#F59E0B', lineHeight: 1 }}>
                    {questionResult.optionBCount || 0}
                  </div>
                  <div style={{ fontSize: 'clamp(1.2rem, 3vw, 1.5rem)', color: '#F59E0B', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                    B
                  </div>
                  <div style={{ fontSize: 'clamp(0.9rem, 2vw, 1rem)', opacity: 0.6, marginTop: '0.25rem', color: '#fff', maxWidth: '300px', lineHeight: '1.3' }}>
                    {optionB}
                  </div>
                  <div style={{ fontSize: 'clamp(0.9rem, 2vw, 1rem)', opacity: 0.6, marginTop: '0.25rem', color: '#fff' }}>
                    {totalChoices > 0 ? Math.round(((questionResult.optionBCount || 0) / totalChoices) * 100) : 0}%
                  </div>
                </div>
              </div>

              {/* Choice Distribution Bar */}
              <div style={{
                display: 'flex',
                height: '24px',
                borderRadius: '12px',
                overflow: 'hidden',
                marginBottom: '2rem',
                background: 'rgba(255,255,255,0.1)',
              }}>
                <div style={{
                  width: `${totalChoices > 0 ? ((questionResult.optionACount || 0) / totalChoices) * 100 : 50}%`,
                  background: '#22C55E',
                  transition: 'width 0.5s ease-out',
                }} />
                <div style={{
                  width: `${totalChoices > 0 ? ((questionResult.optionBCount || 0) / totalChoices) * 100 : 50}%`,
                  background: '#F59E0B',
                  transition: 'width 0.5s ease-out',
                }} />
              </div>

              {/* Who Drinks Section */}
              <div style={{
                background: drinkers.length > 0 ? 'rgba(168, 85, 247, 0.15)' : 'rgba(34, 197, 94, 0.15)',
                border: `3px solid ${drinkers.length > 0 ? 'rgba(168, 85, 247, 0.3)' : 'rgba(34, 197, 94, 0.3)'}`,
                borderRadius: '1.5rem',
                padding: 'clamp(1.5rem, 4vw, 2rem)',
                paddingBottom: '5rem',
                textAlign: 'center',
                marginBottom: '2rem',
                position: 'relative',
                overflow: 'hidden',
              }}>
                <h2 style={{
                  color: drinkers.length > 0 ? '#A855F7' : '#22C55E',
                  marginBottom: '1.5rem',
                  fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                }}>
                  {isTie ? '🍺 TIE! Everyone Drinks! 🍺' : 
                   drinkers.length > 0 ? '🍺 Minority Drinks! 🍺' : 
                   '🎉 Perfect Split! Nobody Drinks! 🎉'}
                </h2>
                
                {drinkers.length > 0 && (
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    gap: '1rem',
                    marginBottom: '3rem',
                  }}>
                    {drinkers.map((drinker, index) => (
                      <div key={drinker.playerId} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        background: 'rgba(0,0,0,0.3)',
                        padding: '1rem 1.5rem',
                        borderRadius: '1rem',
                        animation: `slideIn 0.3s ease-out ${index * 0.1}s both`,
                      }}>
                        {renderAvatar(drinker.avatar, 'clamp(2rem, 5vw, 2.5rem)')}
                        <span style={{ fontSize: 'clamp(1.1rem, 3vw, 1.3rem)', fontWeight: 'bold', color: '#fff' }}>{drinker.playerName}</span>
                        <span style={{ fontSize: 'clamp(1.5rem, 4vw, 2rem)' }}>🍺</span>
                      </div>
                    ))}
                  </div>
                )}

                <BeerTicker />
              </div>

              {/* Debate Prompt */}
              {questionResult.debatePrompt && (
                <div style={{
                  background: 'rgba(168, 85, 247, 0.15)',
                  border: '3px solid rgba(168, 85, 247, 0.3)',
                  borderRadius: '1.5rem',
                  padding: 'clamp(1.2rem, 3vw, 1.5rem)',
                  textAlign: 'center',
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    marginBottom: '0.75rem',
                    fontWeight: 'bold',
                    color: '#A855F7',
                    fontSize: 'clamp(1.1rem, 3vw, 1.3rem)',
                  }}>
                    <span>💬</span>
                    <span>{t('gameplay.hotTakes.debateTime')}</span>
                  </div>
                  <div style={{ fontSize: 'clamp(1rem, 2.5vw, 1.2rem)', lineHeight: '1.6', fontStyle: 'italic', color: '#fff' }}>
                    {questionResult.debatePrompt}
                  </div>
                </div>
              )}
            </div>
          }
          rightContent={
            <StandingsSidebar
              players={roomState.players}
              liveAnswers={sharedLiveAnswers}
              totalPlayers={roomState.players.length}
              questionId={questionResult.questionId}
              shouldUpdateScores={true}
              theme={theme}
            />
          }
          mobileOnlyContent={
            <StandingsSidebar
              players={roomState.players}
              liveAnswers={sharedLiveAnswers}
              totalPlayers={roomState.players.length}
              questionId={questionResult.questionId}
              shouldUpdateScores={true}
              isMobile={true}
              theme={theme}
            />
          }
          rightWidth="320px"
        />
      </div>
      <style>{`
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
    </div>
  );
};
